import React from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';

const AboutContainer = styled.main`
  max-width: 1200px;
  margin: 0 auto;
  padding: 2rem;

  @media (max-width: 768px) {
    padding: 1rem;
  }
`;

const AboutSection = styled.section`
  margin-bottom: 3rem;

  h2 {
    text-align: center;
    margin-bottom: 2rem;
    color: #8b4513;
  }
`;

const AboutContent = styled.div`
  display: flex;
  gap: 3rem;
  align-items: center;

  @media (max-width: 768px) {
    flex-direction: column;
  }
`;

const AboutText = styled.div`
  flex: 1;

  p {
    margin-bottom: 1rem;
    font-size: 1.1rem;
    line-height: 1.6;
  }
`;

const AboutImage = styled.div`
  flex: 1;

  img {
    width: 100%;
    border-radius: 10px;
    box-shadow: 0 5px 15px rgba(0,0,0,0.1);
  }
`;

const Team = styled.section`
  margin-bottom: 3rem;

  h2 {
    text-align: center;
    margin-bottom: 2rem;
    color: #8b4513;
  }
`;

const TeamGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 2rem;
`;

const TeamMember = styled(motion.div)`
  text-align: center;
  background-color: white;
  padding: 2rem;
  border-radius: 10px;
  box-shadow: 0 5px 15px rgba(0,0,0,0.1);

  img {
    width: 150px;
    height: 150px;
    border-radius: 50%;
    object-fit: cover;
    margin-bottom: 1rem;
  }

  h3 {
    margin-bottom: 0.5rem;
    color: #8b4513;
  }

  p:first-of-type {
    font-weight: bold;
    color: #d2691e;
    margin-bottom: 0.5rem;
  }

  p:last-of-type {
    color: #666;
    line-height: 1.5;
  }
`;

const About = () => {
  const teamMembers = [
    {
      id: 1,
      image: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=300',
      name: 'Chef Maria Rodriguez',
      role: 'Head Baker & Owner',
      bio: 'With 20 years of experience, Maria leads our kitchen with creativity and precision.'
    },
    {
      id: 2,
      image: 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=300',
      name: 'Alex Chen',
      role: 'Cake Decorator',
      bio: 'Alex\'s artistic eye brings our cakes to life with stunning designs and colors.'
    },
    {
      id: 3,
      image: 'https://images.unsplash.com/photo-1494790108755-2616b612b786?w=300',
      name: 'Sarah Johnson',
      role: 'Customer Service Manager',
      bio: 'Sarah ensures every client feels welcomed and their needs are met with care.'
    }
  ];

  return (
    <AboutContainer>
      <AboutSection as={motion.section} initial={{ opacity: 0, y: 50 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }}>
        <h2>Our Story</h2>
        <AboutContent>
          <AboutText>
            <p>Founded in 2010, Sweet Delights Bakery has been crafting unforgettable custom cakes and desserts for over a decade. What started as a small family passion has grown into a beloved local institution, known for our attention to detail and commitment to quality.</p>
            <p>Our team of skilled bakers and decorators work tirelessly to bring your visions to life, whether it's a simple birthday cake or an elaborate wedding centerpiece. We use only the finest ingredients, sourced locally whenever possible, to ensure every bite is a delight.</p>
            <p>From classic flavors to innovative creations, we pride ourselves on making each dessert uniquely yours. Come visit us and let us sweeten your special moments!</p>
          </AboutText>
          <AboutImage>
            <img src="https://images.unsplash.com/photo-1556909114-f6e7ad7d3136?w=600" alt="Bakery interior" />
          </AboutImage>
        </AboutContent>
      </AboutSection>

      <Team>
        <h2>Meet Our Team</h2>
        <TeamGrid>
          {teamMembers.map((member, index) => (
            <TeamMember
              key={member.id}
              initial={{ opacity: 0, y: 50 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: index * 0.2 }}
              whileHover={{ scale: 1.05 }}
            >
              <img src={member.image} alt={member.name} />
              <h3>{member.name}</h3>
              <p>{member.role}</p>
              <p>{member.bio}</p>
            </TeamMember>
          ))}
        </TeamGrid>
      </Team>
    </AboutContainer>
  );
};

export default About;